import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  ButtonGroup,
  Card,
  CardMedia,
  CardContent,
  Rating,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Paper
} from '@mui/material';
import CssBaseline from "@mui/material/CssBaseline";
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import useGetLatestStore from '../../../api-manage/hooks/react-query/store/useGetLatestStore';
import { useSelector } from 'react-redux';
import { useRouter } from '../../../helper-functions/useRouterReplacement';
import { removeDuplicates } from '../../../utils/CustomFunctions';
import MainLayout from '../../../components/layout/MainLayout';
import SEO from '../../../components/seo';
import useConfigData from "../../../hooks/useConfigData";

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
  color: 'white',
  padding: theme.spacing(6, 0),
  marginBottom: theme.spacing(4),
}));

const StoreCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StoreImage = styled(CardMedia)({
  height: 200,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
});

const LatestStoreContent = () => {
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [storeData, setStoreData] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(null);
  
  const router = useRouter();
  const { configData } = useSelector((state) => state.configData);
  
  // Use the latest stores API
  const { data, refetch, isLoading, error } = useGetLatestStore();

  // Debug logging
  console.log('=== LatestStorePage Debug ===');
  console.log('data:', data);
  console.log('isLoading:', isLoading);
  console.log('error:', error);
  console.log('filterType:', filterType);
  console.log('storeData:', storeData);
  console.log('filteredStores:', filteredStores);
  console.log('totalDataCount:', totalDataCount);
  console.log('configData:', configData);
  console.log('==============================');

  // Process API response data
  const handleStoreData = () => {
    if (data) {
      const stores = data?.stores || data?.data || [];
      setStoreData(stores);
      setTotalDataCount(data?.total_size || stores.length);
    }
  };

  useEffect(() => {
    handleStoreData();
  }, [data]);

  // Trigger initial fetch
  useEffect(() => {
    refetch();
  }, [filterType, refetch]);

  // Apply search filter to processed store data
  useEffect(() => {
    if (storeData?.length > 0) {
      let stores = storeData;
      
      // Apply search filter
      if (searchTerm) {
        stores = stores.filter(store =>
          store.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          store.address?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setFilteredStores(stores);
    } else {
      setFilteredStores([]);
    }
  }, [storeData, searchTerm]);

  const handleStoreClick = (storeId, storeSlug) => {
    router.push(`/store/${storeSlug || storeId}`);
  };

  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
  };

  const renderStoreCard = (store) => {
    const imageUrl = store.cover_photo 
      ? `${configData?.base_urls?.store_cover_photo_url}/${store.cover_photo}`
      : 'https://via.placeholder.com/400x200/f5f5f5/9e9e9e?text=Store+Image';

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={store.id}>
        <StoreCard onClick={() => handleStoreClick(store.id, store.slug)}>
          <StoreImage
            image={imageUrl}
            title={store.name}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x200/f5f5f5/9e9e9e?text=Store+Image';
            }}
          />
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="h2" gutterBottom noWrap>
              {store.name}
            </Typography>
            
            <Box display="flex" alignItems="center" mb={1}>
              <LocationOnIcon color="action" sx={{ fontSize: 16, mr: 0.5 }} />
              <Typography variant="body2" color="text.secondary" noWrap>
                {store.address}
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" mb={1}>
              <Rating
                value={parseFloat(store.avg_rating || 0)}
                precision={0.1}
                size="small"
                readOnly
              />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({store.rating_count || 0})
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" mb={1}>
              <AccessTimeIcon color="action" sx={{ fontSize: 16, mr: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                {store.delivery_time || '30-45'} min
              </Typography>
            </Box>
            
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
              {store.delivery && (
                <StatsChip
                  label="Delivery"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {store.take_away && (
                <StatsChip
                  label="Takeaway"
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
              {store.free_delivery && (
                <StatsChip
                  label="Free Delivery"
                  size="small"
                  color="success"
                  variant="filled"
                  icon={<LocalOfferIcon />}
                />
              )}
            </Box>
          </CardContent>
        </StoreCard>
      </Grid>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading latest stores...
          </Typography>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load stores: {error.message || 'Unknown error'}
          </Alert>
          <Button variant="contained" onClick={() => refetch()}>
            Try Again
          </Button>
        </Box>
      );
    }

    if (!filteredStores || filteredStores.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" gutterBottom>
            No stores found
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {searchTerm 
              ? `No stores match "${searchTerm}". Try a different search term.`
              : 'No latest stores available at the moment.'
            }
          </Typography>
          {searchTerm && (
            <Button 
              variant="outlined" 
              onClick={() => setSearchTerm('')}
              sx={{ mt: 2 }}
            >
              Clear Search
            </Button>
          )}
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {filteredStores.map(renderStoreCard)}
      </Grid>
    );
  };

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom align="center">
            Latest Stores
          </Typography>
          <Typography variant="h6" align="center" sx={{ opacity: 0.9 }}>
            Discover the newest stores in your area
          </Typography>
          {totalDataCount && (
            <Typography variant="body1" align="center" sx={{ mt: 2, opacity: 0.8 }}>
              {totalDataCount} stores available
            </Typography>
          )}
        </Container>
      </HeroSection>

      <Container maxWidth="lg">
        {/* Filter and Search Section */}
        <FilterSection elevation={1}>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
            <Box flex={1}>
              <TextField
                fullWidth
                placeholder="Search stores..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <ButtonGroup variant="outlined" color="primary">
              <Button
                variant={filterType === 'all' ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange('all')}
              >
                All
              </Button>
              <Button
                variant={filterType === 'delivery' ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange('delivery')}
              >
                Delivery
              </Button>
              <Button
                variant={filterType === 'take_away' ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange('take_away')}
              >
                Takeaway
              </Button>
            </ButtonGroup>
          </Box>
        </FilterSection>

        {/* Stores Grid */}
        {renderContent()}
      </Container>
    </Box>
  );
};

const LatestStorePage = () => {
  const { configData, landingPageData, loading } = useConfigData();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      <CssBaseline />
      <SEO
        title={configData ? `Latest Stores` : "Loading..."}
        image={`${configData?.base_urls?.business_logo_url}/${configData?.fav_icon}`}
        businessName={configData?.business_name}
      />
      <MainLayout configData={configData} landingPageData={landingPageData}>
        <LatestStoreContent />
      </MainLayout>
    </>
  );
};

export default LatestStorePage;
